import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { IKpu, IObject, ITank, ITransferData } from 'interfaces/applicant.interface';
import Icon from '../../components/icon/icon.component';
import { updateTank } from '../../requests/dashboard.request';
import TransferModal from './transfer.modal';
import Loader from '../../components/loader/loader.component';
import { useMain } from '../../contexts/main.context';
import { removeEmptyStrings } from '../../utils/form.utils';

interface IProps {
  object: IObject;
  kpu: IKpu;
  dictionaries: Record<string, string[]>;
  handleExpandNewKpu: (kpuId: number) => void;
  handleChange: (
    tankId: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => void;
}

const TankTable: React.FC<IProps> = ({
  object,
  kpu,
  dictionaries,
  handleExpandNewKpu,
  handleChange,
}) => {
  const { t } = useTranslation();
  const { handleGetApplicant } = useMain();
  const [loading, setLoading] = useState(false);
  const [transferData, setTransferData] = useState<ITransferData | null>(null);

  const handleSaveTank = (tank: ITank) => () => {
    setLoading(true);
    const dto = removeEmptyStrings(tank) as ITank;
    updateTank(dto)
      .then((res) => {
        if (res.success) {
          toast.success('Резервуар успешно сохранен');
        }
      })
      .catch(() => toast.error('Ошибка при сохранении резервуара'))
      .finally(() => setLoading(false));
  };

  const handleOpenTransferModal = (object: IObject, tank: ITank) => () => {
    setTransferData({ kpuList: object.kpus, tank });
  };

  const handleCloseTransferModal = (kpuId?: number) => {
    setTransferData(null);
    if (kpuId) {
      handleGetApplicant();
      handleExpandNewKpu(kpuId);
    }
  };

  return (
    <>
      <Loader loading={loading}>
        <table className="dashboard__table">
          <thead>
            <tr>
              <th>ID резервуара в Qoldau</th>
              <th>Название</th>
              <th>Порядковый номер резервуара для данного IP КПУ</th>
              <th>Тип ГСМ</th>
              <th>Ед. изм. объема</th>
              <th>Ед. изм. уровня резервуара</th>
              <th>Ед. изм. массы</th>
              <th style={{ width: '100px' }}></th>
            </tr>
          </thead>
          <tbody>
            {kpu.tanks?.map((tank: ITank) => (
              <tr key={tank.tankId}>
                <td>{tank.qoldauTankId}</td>
                <td>
                  <input
                    type="text"
                    name="name"
                    value={tank.name || ''}
                    autoComplete="off"
                    onChange={(e) => handleChange(tank.tankId, e)}
                  />
                </td>
                <td>
                  <select
                    name="deviceNum"
                    value={tank.deviceNum || ''}
                    onChange={(e) => handleChange(tank.tankId, e)}
                  >
                    <option value="">Выберите</option>
                    {[...Array(16)].map((_, index) => (
                      <option key={index + 1} value={index + 1}>
                        {index + 1}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    name="oilProductType"
                    value={tank.oilProductType ?? ''}
                    onChange={(e) => handleChange(tank.tankId, e)}
                  >
                    <option value="">Выберите</option>
                    {dictionaries['oil-product-types']?.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    name="volumeUnitType"
                    value={tank.volumeUnitType ?? ''}
                    onChange={(e) => handleChange(tank.tankId, e)}
                  >
                    <option value="">Выберите</option>
                    {dictionaries['volume-unit-types']?.map((type) => (
                      <option key={type} value={type}>
                        {t(`enum.volumeUnit.${type}`)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    name="levelUnitType"
                    value={tank.levelUnitType ?? ''}
                    onChange={(e) => handleChange(tank.tankId, e)}
                  >
                    <option value="">Выберите</option>
                    {dictionaries['level-unit-types']?.map((type) => (
                      <option key={type} value={type}>
                        {t(`enum.levelUnit.${type}`)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    name="massUnitType"
                    value={tank.massUnitType ?? ''}
                    onChange={(e) => handleChange(tank.tankId, e)}
                  >
                    <option value="">Выберите</option>
                    {dictionaries['mass-unit-types']?.map((type) => (
                      <option key={type} value={type}>
                        {t(`enum.massUnit.${type}`)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <div className="dashboard__btnWrapper">
                    <button
                      type="button"
                      className="dashboard__saveBtn"
                      onClick={handleSaveTank(tank)}
                      title={'Сохранить'}
                    >
                      <Icon name={'Save'} />
                    </button>
                    <button
                      type="button"
                      className="dashboard__moveBtn"
                      onClick={handleOpenTransferModal(object, tank)}
                      title={'Переместить в другой КПУ'}
                    >
                      <Icon name={'Forward'} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!kpu.tanks || (kpu.tanks && !kpu.tanks.length) ? (
              <tr>
                <td colSpan={8} className="dashboard__notFound">
                  Резерувуары отсутствуют
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Loader>
      {!!transferData?.tank && (
        <TransferModal
          handleClose={handleCloseTransferModal}
          data={transferData as ITransferData}
        />
      )}
    </>
  );
};

export default TankTable;
